import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Package, Sliders, Layout } from 'lucide-react';
import { Product } from '@jigflow/shared';
import { useProducts } from '../../contexts/ProductContext';
import GeneralTab from './GeneralTab';
import CustomizationTab from './CustomizationTab';
import TemplateEditor from './svg-editor/TemplateEditor';
import type { TemplateEditorHandle } from './svg-editor/TemplateEditor';

interface ProductEditorProps {
    product: Product | null; // null = creating new product
    isOpen: boolean;
    onClose: () => void;
    onSave: (product: Product) => void;
}

type TabId = 'general' | 'customization' | 'templates';

interface Tab {
    id: TabId;
    label: string;
    icon: typeof Package;
}

const TABS: Tab[] = [
    { id: 'general', label: 'General', icon: Package },
    { id: 'customization', label: 'Customization', icon: Sliders },
    { id: 'templates', label: 'Templates', icon: Layout },
];

export default function ProductEditor({ product, isOpen, onClose, onSave }: ProductEditorProps) {
    const { categories, addProduct, deleteProduct } = useProducts();
    const [activeTab, setActiveTab] = useState<TabId>('general');
    const [editedProduct, setEditedProduct] = useState<Product | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isDraftProduct, setIsDraftProduct] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const draftIdRef = useRef<string | null>(null);
    const templateEditorRef = useRef<TemplateEditorHandle>(null);

    // Initialize/reset when product changes
    useEffect(() => {
        if (isOpen) {
            if (product) {
                // Editing existing product
                setEditedProduct({ ...product });
                setIsDraftProduct(false);
                draftIdRef.current = null;
            } else {
                // Creating new product - create a draft in the database
                // Must await to get the server-generated ID
                const createDraft = async () => {
                    try {
                        const created = await addProduct({
                            name: 'New Product',
                            description: '',
                            price: 0,
                            image: '',
                            images: [],
                            category: '',
                            isDraft: true,
                        });
                        setEditedProduct(created);
                        setIsDraftProduct(true);
                        draftIdRef.current = created.id;
                    } catch (err) {
                        console.error('[ProductEditor] Failed to create draft:', err);
                    }
                };
                createDraft();
            }
            setActiveTab('general');
            setHasChanges(false);
        }
    }, [isOpen, product, categories, addProduct]);

    // Cleanup function for when component unmounts or closes without saving
    const cleanupDraft = async () => {
        if (isDraftProduct && draftIdRef.current) {
            await deleteProduct(draftIdRef.current);
            draftIdRef.current = null;
        }
    };

    if (!isOpen || !editedProduct) return null;

    const handleProductChange = (updates: Partial<Product>) => {
        setEditedProduct(prev => prev ? { ...prev, ...updates } : null);
        setHasChanges(true);
    };

    const handleSave = async () => {
        if (!editedProduct) return;
        setIsSaving(true);

        try {
            // Flush template editor changes to DB
            if (templateEditorRef.current) {
                await templateEditorRef.current.saveAll();
            }

            // Save product (General/Customization tab changes)
            const productToSave = { ...editedProduct, isDraft: false };
            onSave(productToSave);
            draftIdRef.current = null;
            setIsDraftProduct(false);
            onClose();
        } catch (error) {
            console.error('[ProductEditor] Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        const doClose = () => {
            cleanupDraft();
            onClose();
        };

        // Check both General/Customization changes AND template changes
        const templateHasChanges = templateEditorRef.current?.hasUnsavedChanges() ?? false;
        const anyChanges = hasChanges || templateHasChanges;

        if (anyChanges) {
            if (confirm('You have unsaved changes. Are you sure you want to close? Any draft data will be discarded.')) {
                doClose();
            }
        } else {
            doClose();
        }
    };

    const isNewProduct = isDraftProduct;

    // Check if template has unsaved changes (for the indicator)
    const templateHasChanges = templateEditorRef.current?.hasUnsavedChanges() ?? false;
    const anyUnsavedChanges = hasChanges || templateHasChanges;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
                {/* Header - Compact */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                        <Package className="text-blue-600" size={16} />
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-white">
                            {isNewProduct ? 'New Product' : `Edit: ${product?.name}`}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Tabs - All tabs now always enabled */}
                <div className="flex border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    {TABS.map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
                                    isActive
                                        ? 'border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                }`}
                            >
                                <Icon size={16} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'general' && (
                        <GeneralTab
                            product={editedProduct}
                            onChange={handleProductChange}
                        />
                    )}
                    {activeTab === 'customization' && (
                        <CustomizationTab productId={editedProduct.id} />
                    )}
                    {activeTab === 'templates' && (
                        <TemplateEditor ref={templateEditorRef} productId={editedProduct.id} />
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                        {anyUnsavedChanges && (
                            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                                Unsaved changes
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleClose}
                            className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!editedProduct.name.trim() || isSaving}
                            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-medium rounded-lg transition disabled:cursor-not-allowed"
                        >
                            <Save size={16} />
                            {isSaving ? 'Saving...' : isNewProduct ? 'Create Product' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
